import IconEthWhite from 'common/assets/image/ichi/icons/dark/icon_white_eth.svg';
import IconMaticWhite from 'common/assets/image/ichi/icons/dark/icon_white_matic.svg';
import IconEth from 'common/assets/image/ichi/icons/light/eth_black.svg';
import IconPreferencesActive from 'common/assets/image/ichi/icons/light/icon_active_settings.svg';
import IconClose from 'common/assets/image/ichi/icons/light/icon_close.svg';
import IconFuse from 'common/assets/image/ichi/icons/light/icon_fuse.svg';
import IconIchiOwned from 'common/assets/image/ichi/icons/light/icon_ichi_blue.svg';
import IconIchiMobile from 'common/assets/image/ichi/icons/light/icon_ichi_mobile.svg';
import IconIchiApy from 'common/assets/image/ichi/icons/light/icon_ichi_percentage.svg';
import IconIchiWhite from 'common/assets/image/ichi/icons/light/icon_ichi_white.svg';
import IconXIchiIchiRatio from 'common/assets/image/ichi/icons/light/icon_ichi_xichi.svg';
import IconPreferencesInactive from 'common/assets/image/ichi/icons/light/icon_inactive_settings.svg';
import IconPreferencesInactiveMobile from 'common/assets/image/ichi/icons/light/icon_inactive_settings_mobile.svg';
import IconHelp from 'common/assets/image/ichi/icons/light/icon_info.svg';
import IconLp from 'common/assets/image/ichi/icons/light/icon_lp.svg';
import IconNewWindow from 'common/assets/image/ichi/icons/light/icon_new_window.svg';
import IconNewWindowWhite from 'common/assets/image/ichi/icons/light/icon_new_window_white.svg';
import IconRenFil from 'common/assets/image/ichi/icons/light/icon_renFIL.svg';
import IconWBTC from 'common/assets/image/ichi/icons/light/icon_wbtc.svg';
import IconXIchiOwned from 'common/assets/image/ichi/icons/light/icon_xichi_green.svg';
import IconSidebar from 'common/assets/image/ichi/logo_horizontal_light/ichi_logo_horizontal_light.svg';
import IconTheme from 'common/assets/image/ichi/theme/light_mode.svg';
import IconThemeMobile from 'common/assets/image/ichi/theme/light_mode_mobile.svg';
import BadgePolygon from 'common/assets/image/polygon/light_polygon_badge.svg';
import { ColorsKey, IchiTheme, Theme } from 'common/models/theme';
import { css } from 'styled-components';
import lightColors from './lightColors';

const MEDIA_WIDTHS = {
  upToExtraSmall: 500,
  upToSmall: 720,
  upToMedium: 960,
  upToLarge: 1280,
};

const mediaWidthTemplates: { [width in keyof typeof MEDIA_WIDTHS]: typeof css } = Object.keys(MEDIA_WIDTHS).reduce((accumulator, size) => {
  (accumulator as any)[size] = (a: any, b: any, c: any) => css`
    @media (max-width: ${(MEDIA_WIDTHS as any)[size]}px) {
      ${css(a, b, c)}
    }
  `;
  return accumulator;
}, {}) as any;

export const lightTheme: IchiTheme = {
  name: Theme.LIGHT, // media queries
  mediaWidth: mediaWidthTemplates,
  icons: {
    sidebar: IconSidebar,
    theme: IconTheme,
    themeMobile: IconThemeMobile,
    preferences: {
      active: IconPreferencesActive,
      inactive: IconPreferencesInactive,
      inactiveMobile: IconPreferencesInactiveMobile
    },
    help: IconHelp,
    ichiWhite: IconIchiWhite,
    ichiMobile: IconIchiMobile,
    ichiOwned: IconIchiOwned,
    xIchiOwned: IconXIchiOwned,
    xIchiIchiRatio: IconXIchiIchiRatio,
    ichiApy: IconIchiApy,
    close: IconClose,
    newWindow: IconNewWindow,
    newWindowWhite: IconNewWindowWhite,
    lp: IconLp,
    eth: IconEth,
    ethWhite: IconEthWhite,
    maticWhite: IconMaticWhite,
    wbtc: IconWBTC,
    renFIL: IconRenFil,
    fuse: IconFuse,
    polygonBadge: BadgePolygon,
  },
  breakpoints: ['576px', '768px', '991px', '1220px'],
  space: [0, 5, 8, 10, 15, 20, 25, 30, 33, 35, 40, 50, 60, 70, 80, 85, 90, 100],
  fontSizes: [10, 12, 14, 15, 16, 18, 20, 22, 24, 36, 48, 80, 96],
  fontWeights: [100, 200, 300, 400, 500, 600, 700, 800, 900],
  lineHeights: {
    solid: 1,
    title: 1.25,
    copy: 1.5,
  },
  letterSpacings: {
    normal: 'normal',
    tracked: '0.1em',
    tight: '-0.05em',
    mega: '0.25em',
  },
  borders: [0, '1px solid', '2px solid', '3px solid', '4px solid', '5px solid', '6px solid'],
  radius: [3, 4, 5, 10, 20, 30, 60, 120, '50%'],
  widths: [36, 40, 44, 48, 54, 70, 81, 128, 256],
  heights: [36, 40, 44, 46, 48, 54, 70, 81, 128],
  maxWidths: [16, 32, 64, 128, 256, 512, 768, 1024, 1536],
  colors: lightColors,
  // Styles
  colorStyles: {
    primaryText: {
      color: lightColors.black as ColorsKey,
    },
    primary: {
      color: lightColors.primary as ColorsKey,
      borderColor: lightColors.transparent as ColorsKey,
      backgroundColor: lightColors.transparent as ColorsKey,
      '&:hover': {
        color: lightColors.primary as ColorsKey,
        backgroundColor: lightColors.transparent as ColorsKey,
      },
    },
    secondary: {
      color: lightColors.secondary as ColorsKey,
      borderColor: lightColors.transparent as ColorsKey,
      backgroundColor: lightColors.transparent as ColorsKey,
      '&:hover': {
        color: lightColors.secondaryHover as ColorsKey,
        borderColor: lightColors.transparent as ColorsKey,
        backgroundColor: lightColors.transparent as ColorsKey,
      },
    },
    warning: {
      color: lightColors.yellow as ColorsKey,
      borderColor: lightColors.yellow as ColorsKey,
      '&:hover': {
        color: lightColors.yellowHover as ColorsKey,
        borderColor: lightColors.yellowHover as ColorsKey,
      },
    },
    error: {
      color: lightColors.secondaryHover as ColorsKey,
      borderColor: lightColors.secondaryHover as ColorsKey,
      '&:hover': {
        color: lightColors.secondary as ColorsKey,
        borderColor: lightColors.secondary as ColorsKey,
      },
    },
    primaryWithBg: {
      color: lightColors.white as ColorsKey,
      backgroundColor: lightColors.primary as ColorsKey,
      borderColor: lightColors.primary as ColorsKey,
      border: '1px solid',
      '&:hover': {
        color: lightColors.white as ColorsKey,
        backgroundColor: lightColors.primaryHover as ColorsKey,
        borderColor: lightColors.primaryHover as ColorsKey,
        boxShadow: lightColors.primaryBoxShadow as ColorsKey,
      },
    },
    secondaryWithBg: {
      color: lightColors.black as ColorsKey,
      backgroundColor: lightColors.secondary as ColorsKey,
      borderColor: lightColors.secondary as ColorsKey,
      '&:hover': {
        color: lightColors.black as ColorsKey,
        backgroundColor: lightColors.secondaryHover as ColorsKey,
        borderColor: lightColors.secondaryHover as ColorsKey,
        boxShadow: lightColors.secondaryBoxShadow as ColorsKey,
      },
    },
    warningWithBg: {
      color: lightColors.white as ColorsKey,
      backgroundColor: lightColors.yellow as ColorsKey,
      borderColor: lightColors.yellow as ColorsKey,
      '&:hover': {
        backgroundColor: lightColors.yellowHover as ColorsKey,
        borderColor: lightColors.yellowHover as ColorsKey,
      },
    },
    errorWithBg: {
      color: lightColors.white as ColorsKey,
      backgroundColor: lightColors.secondaryHover as ColorsKey,
      borderColor: lightColors.secondaryHover as ColorsKey,
      '&:hover': {
        backgroundColor: lightColors.secondary as ColorsKey,
        borderColor: lightColors.secondary as ColorsKey,
      },
    },
    transparentBg: {
      backgroundColor: lightColors.white as ColorsKey,
      '&:hover': {
        backgroundColor: lightColors.white as ColorsKey,
      },
    },
  },
  buttonStyles: {
    primaryButton: {
      border: 'none',
      borderRadius: '20px',
      color: lightColors.primaryButtonColor as ColorsKey,
      backgroundColor: lightColors.primaryButtonBgColor as ColorsKey,
      height: 'auto',
      padding: '10px',
      '&:hover': {
        cursor: 'pointer',
        textDecoration: 'underline',
      },
    },
    textButton: {
      border: 0,
      color: lightColors.primary as ColorsKey,
      padding: 0,
      height: 'auto',
      backgroundColor: lightColors.transparent as ColorsKey,
    },
    outlined: {
      borderWidth: '1px',
      borderStyle: 'solid',
      backgroundColor: lightColors.transparent as ColorsKey,
    },
    fab: {
      border: '0',
      width: '40px',
      height: '40px',
      padding: 0,
      borderRadius: '50%',
      justifyContent: 'center',
      'span.btn-icon': {
        paddingLeft: 0,
      },
    },
    extendedFab: {
      border: '0',
      minWidth: '50px',
      height: '40px',
      borderRadius: '50px',
      justifyContent: 'center',
    },
  },
  // FlexBox: {
  //   backgroundColor: 'green'
  // }
};
